/**
 * App icons — single source of truth. Fully local SVG components; no npm icon
 * package and no license key.
 *
 * Each export keeps its original Untitled-UI-era name so the ~49 call sites are
 * unchanged. One local source, one Central variant — the square-cornered
 * Line/Fill family (square corners, `strokeLinecap="square"`, stroke 2):
 *  - `./icons-local/generated-central` — every Central glyph we use, vendored as
 *    local source. Bare `IconFoo` is the Line (outlined) shape; `IconFooFill` is
 *    the Fill (solid) shape nav items show when active. Add a glyph by editing
 *    OUTLINED/FILLED in scripts/vendor-central-icons.mjs and re-running it —
 *    never by pasting an SVG export.
 *
 * Props: every icon is `React.FC<{ size?: string | number } & SVGProps<SVGSVGElement>>`
 * — `size`, `className`, `color`, `strokeWidth`, `style`, `onClick`, etc. all work.
 */
import {
  IconAnalytics,
  IconArrowDownWall,
  IconArrowLeft,
  IconArrowRightSquare,
  IconArrowRotateClockwise,
  IconArrowRotateCounterClockwise,
  IconArrowUpWall,
  IconBookmarkCheck,
  IconBookmarkPlus,
  IconBubbleDots,
  IconBubblePlus,
  IconBubbleText,
  IconBubbleText6,
  IconBarsThree,
  IconBuildings,
  IconCalendar1,
  IconCalendarCheck,
  IconCalendarRemove4,
  IconCheckmark1,
  IconChevronDownSmall,
  IconChevronLeft,
  IconChevronRight,
  IconChevronTop,
  IconCircleCheck,
  IconCircleInfo,
  IconCircleX,
  IconClipboard,
  IconCloudUpload,
  IconCreditCard2,
  IconDotGrid1x3Vertical,
  IconEmail1,
  IconExclamationCircle,
  IconExclamationTriangle,
  IconEyeOpen,
  IconFileText,
  IconFingerPrint1,
  IconGlobe,
  IconHome,
  IconLayoutRight,
  IconListBullets,
  IconLoadingCircle,
  IconMagnifyingGlass,
  IconMapPin,
  IconMinusMedium,
  IconPlusMedium,
  IconPaperclip1,
  IconPencil,
  IconPeopleAdd,
  IconPhone,
  IconReceiptBill,
  IconRecord,
  IconSend,
  IconServer,
  IconShoppingBag3,
  IconTrashCan,
  IconUser,
  IconUserGroup,
  IconX,
} from './icons-local/generated-central';
import { createSvgIcon } from './icons-local/createSvgIcon';

// Left = original Untitled UI name (kept so call sites are unchanged).
export const AlertCircle = IconExclamationCircle;
export const AlertTriangle = IconExclamationTriangle;
export const ArrowLeft = IconArrowLeft;
export const BarChartSquare02 = IconAnalytics;
export const BookmarkAdd = IconBookmarkPlus;
export const BookmarkCheck = IconBookmarkCheck;
export const Building07 = IconBuildings;
export const Calendar = IconCalendar1;
export const CalendarCheck01 = IconCalendarCheck;
export const CalendarRemove = IconCalendarRemove4;
export const Check = IconCheckmark1;
export const CheckCircle = IconCircleCheck;
export const ChevronDown = IconChevronDownSmall;
export const ChevronLeft = IconChevronLeft;
export const ChevronRight = IconChevronRight;
export const ChevronUp = IconChevronTop;
export const Clipboard = IconClipboard;
export const CreditCard02 = IconCreditCard2;
// There is deliberately no `Circle` export. The Central set's `IconCircle` is
// not a circle — it is a spiral ("circle, line, paint" upstream) that fills
// into a blob under a `svg { fill }` rule, and it was mistaken for a circle
// twice. Use `Dot` or `DotSolid` for round shapes and do not re-add `Circle`.
//
// `Dot` is a ring around a centre dot (the Central "record" glyph). Reads well
// as a size affordance at 10px and up.
export const Dot = IconRecord;
// A plain solid dot, hand-authored because the Central set has none: at the 8px
// the radio/dropdown indicators use, a ring reads as a busy target rather than
// a selected dot.
export const DotSolid = createSvgIcon(
  <circle cx="12" cy="12" r="7" fill="currentColor" />,
  'DotSolid'
);
export const DotsVertical = IconDotGrid1x3Vertical;
export const Download01 = IconArrowDownWall;
export const Edit01 = IconPencil;
export const Eye = IconEyeOpen;
export const File02 = IconFileText;
export const Fingerprint01 = IconFingerPrint1;
export const Globe01 = IconGlobe;
export const Home01 = IconHome;
export const InfoCircle = IconCircleInfo;
export const LayoutRight = IconLayoutRight;
// Bulleted-list glyph — the "list view" affordance (e.g. the collections
// calendar/list toggle). Distinct from `Menu02` (a hamburger), so the two
// never share one glyph.
export const List = IconListBullets;
export const Loading01 = IconLoadingCircle;
export const LogOut04 = IconArrowRightSquare;
export const Mail01 = IconEmail1;
export const MarkerPin01 = IconMapPin;
// `Menu02` is a hamburger (three equal bars) — used for the mobile nav toggle.
// Previously aliased to `IconBurger`, which is a literal food-burger glyph
// (bun + patty), so it rendered a sandwich instead of a menu. Use `List` for
// list/rows affordances, not this.
export const Menu02 = IconBarsThree;
export const MessageChatCircle = IconBubbleText;
export const MessageDotsCircle = IconBubbleDots;
export const MessagePlusCircle = IconBubblePlus;
export const MessageTextSquare01 = IconBubbleText6;
export const Minus = IconMinusMedium;
export const Plus = IconPlusMedium;
export const Paperclip = IconPaperclip1;
export const Phone = IconPhone;
export const Receipt = IconReceiptBill;
export const RefreshCcw01 = IconArrowRotateCounterClockwise;
export const RefreshCw01 = IconArrowRotateClockwise;
export const SearchLg = IconMagnifyingGlass;
export const Send01 = IconSend;
export const Server01 = IconServer;
export const ShoppingBag03 = IconShoppingBag3;
export const Trash01 = IconTrashCan;
export const Upload01 = IconArrowUpWall;
export const UploadCloud01 = IconCloudUpload;
export const User01 = IconUser;
export const Users01 = IconUserGroup;
export const UsersPlus = IconPeopleAdd;
export const XClose = IconX;
export const XCircle = IconCircleX;
